import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useNotifStore } from '@/stores/notifications';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ChatBar } from '@/components/ChatBar';
import { MobileNav } from '@/components/MobileNav';
import { LandingPage } from '@/pages/LandingPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { FeedPage } from '@/pages/FeedPage';
import {
  MessagesPage,
  EventsPage,
  DirectoryPage,
  FriendsPage,
  NotificationsPage,
} from '@/pages/StubPages';
import { ProfilePage } from '@/pages/ProfilePage';
import { GroupsPage } from '@/pages/GroupsPage';
import { GroupDetailPage } from '@/pages/GroupDetailPage';
import { NotesPage } from '@/pages/NotesPage';
import { AlbumPage } from '@/pages/AlbumPage';

function AuthenticatedLayout() {
  const { user, loading, initialized } = useAuthStore();
  const { pollUnread } = useNotifStore();

  // Poll notifications every 30s
  useEffect(() => {
    if (!user) return;
    pollUnread();
    const interval = setInterval(pollUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-sand-200 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md mx-auto mb-3 animate-pulse">
            <span className="text-2xl">{'\u301c\ufe0f'}</span>
          </div>
          <p className="text-sand-500 text-sm">Tuning in {'\u223f'}</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  // If user hasn't completed onboarding (no display_name)
  if (!user.display_name) return <Navigate to="/onboarding" replace />;

  return (
    <div className="min-h-screen bg-sand-200 pb-14 md:pb-0">
      <Header />
      <Outlet />
      <Footer />
      <ChatBar />
      <MobileNav />
    </div>
  );
}

export function App() {
  const { fetchUser, initialized } = useAuthStore();

  useEffect(() => {
    fetchUser();
  }, []);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // SW registration failed — fine in dev
      });
    }
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />

        {/* Auth callback — handled by the worker redirect, but just in case */}
        <Route path="/auth/verify" element={<LandingPage />} />

        {/* Authenticated */}
        <Route element={<AuthenticatedLayout />}>
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile/:username" element={<ProfilePage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/groups/:id" element={<GroupDetailPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/notes/:id" element={<NotesPage />} />
          <Route path="/albums/:id" element={<AlbumPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
